import { useRef, useState, useEffect } from "react";
import * as XLSX from "xlsx-js-style";
import { Menu } from "primereact/menu";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Equity from "./Equity";
import Liabilities from "./Liabilities";
import SharedButton from "../../components/Shared/SharedButton";
import CustomBalanceSheetTreeTable from "./CustomBalanceSheetTreeTable";
import { getOneMonthAgo } from "../CashInflow/CashInflow";
import DatePicker from "../../components/Shared/DatePicker";
import useFetchData from "../../hooks/useFetchData";
import CustomTabs from "../../components/Shared/CustomTabs";
import { FaFileExport } from "react-icons/fa6";

export default function BalanceSheetPage() {
  const balanceSheetRef = useRef(null);
  const iframeRef = useRef(null);
  const menuRef = useRef(null);
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const lang = i18next.language;
  const { user } = useSelector((state) => state.auth);
  const {
    data: balanceSheet,
    loading: isLoading,
    executeFetch,
  } = useFetchData();
  const [payload, setPayload] = useState({
    start_date: getOneMonthAgo(
      new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .split("T")[0],
    ),
    end_date: new Date(
      new Date().getTime() - new Date().getTimezoneOffset() * 60000,
    )
      .toISOString()
      .split("T")[0],
  });
  const handlePrint = () => {
    setTimeout(() => {
      const direction = lang === "ar" ? "rtl" : "ltr";
      const iframe = iframeRef.current;
      const doc = iframe.contentWindow.document;

      const businessNameEn = user?.Business?.name_en || "";
      const businessNameAr = user?.Business?.name_ar || "";
      const businessAddress = user?.Business?.address || "";
      const userEmail = user?.email || "";
      const userMobile = user?.mobile_number || "";
      const businessLogo = user?.Business?.logo || user?.Business?.logo_url;

      // Helper: flatten tree accounts recursively
      const flattenAccounts = (nodes = [], level = 0) => {
        let result = [];
        nodes.forEach((node) => {
          result.push({ ...node, level });
          if (node.children && node.children.length > 0) {
            result = result.concat(flattenAccounts(node.children, level + 1));
          }
        });
        return result;
      };

      const thStyle =
        "border: 1px solid #d1d5db; padding: 8px 12px; text-align: center; font-weight: 600; font-size: 12px;";
      const tdStyle =
        "border: 1px solid #d1d5db; padding: 8px 12px; text-align: center; font-size: 12px;";
      const tdNameStyle =
        "border: 1px solid #d1d5db; padding: 8px 12px; text-align: left; font-size: 12px;";

      const buildSectionTable = (title, accounts = [], total = 0) => {
        const rows = flattenAccounts(accounts);
        const rowsHTML = rows
          .map((row) => {
            const paddingLeft = (row.level || 0) * 20 + 8;
            const isBold = (row.level || 0) === 0;
            return `
            <tr style="background:${isBold ? "#f3f4f6" : "#fff"}; font-weight:${isBold ? "600" : "400"};">
              <td style="${tdNameStyle} padding-left:${paddingLeft}px;">${row.account_name || ""}-${row.account_code || ""}</td>
              <td style="${tdStyle}">${row.total_credit ?? "0"} ${t("SAR")}</td>
              <td style="${tdStyle}">${row.total_debit ?? "0"} ${t("SAR")}</td>
              <td style="${tdStyle}">${row.balance ? Number(row.balance).toFixed(2) : "0"} ${t("SAR")}</td>
            </tr>
          `;
          })
          .join("");

        return `
          <div style="margin-bottom: 24px;">
            <div style="background-color: #e5e7eb; padding: 8px 12px; font-weight: 700; font-size: 14px; border: 1px solid #d1d5db; border-bottom: none; border-radius: 4px 4px 0 0;">
              ${title}
            </div>
            <table style="width: 100%; border-collapse: collapse; font-family: sans-serif;">
              <thead>
                <tr style="background-color: #d1d5db;">
                  <th style="${thStyle} text-align:left; width:40%;">${t("name")}</th>
                  <th style="${thStyle} width:20%;">${t("credit")}</th>
                  <th style="${thStyle} width:20%;">${t("debit")}</th>
                  <th style="${thStyle} width:20%;">${t("balance")}</th>
                </tr>
              </thead>
              <tbody>
                ${rowsHTML || `<tr><td colspan="4" style="${tdStyle} text-align:center; color:#6b7280;">-</td></tr>`}
                <tr style="background:#f9fafb; font-weight:600;">
                  <td colspan="3" style="${tdNameStyle} text-align:right;">${title} ${t("Total")}</td>
                  <td style="${tdStyle}">${total ? Number(total).toFixed(2) : "0"} ${t("SAR")}</td>
                </tr>
              </tbody>
            </table>
          </div>
        `;
      };

      const assetsHTML = buildSectionTable(
        t("Assets"),
        balanceSheet?.data?.Assets?.Accounts || [],
        balanceSheet?.data?.Assets?.Total,
      );
      const equityHTML = buildSectionTable(
        t("Equity"),
        balanceSheet?.data?.Equity?.Accounts || [],
        balanceSheet?.data?.Equity?.Total,
      );
      const liabilitiesHTML = buildSectionTable(
        t("Liabilities"),
        balanceSheet?.data?.Liabilities?.Accounts || [],
        balanceSheet?.data?.Liabilities?.Total,
      );

      doc.open();
      doc.write(
        `<html><head><title>Balance Sheet</title><style>@media print {@page {size: A1 portrait; margin: 5mm;} body {margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; font-family: 'Inter', sans-serif; direction: ${direction};} header, footer {display: none;}}</style></head><body style="font-family:sans-serif; direction:${direction}">`,
      );

      // 3-Column Business Header
      doc.write(`
      <div style="display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 20px; border-bottom: 2px solid #ccc; padding-bottom: 10px;">
        
        <!-- Column 1: Contact Info -->
        <div style="flex: 1; text-align: start; font-size: 13px; color: #333; line-height: 1.6;">
          ${businessAddress ? `<div><span style="font-weight: bold;">${t("Address")}:</span> ${businessAddress}</div>` : ""}
          ${userMobile ? `<div><span style="font-weight: bold;">${t("Phone")}:</span> ${userMobile}</div>` : ""}
          ${userEmail ? `<div><span style="font-weight: bold;">${t("Email")}:</span> ${userEmail}</div>` : ""}
        </div>

        <!-- Column 2: Logo and Main Info (Center) -->
        <div style="flex: 1; text-align: center;">
          ${businessLogo ? `<img src="${businessLogo}" style="height: 75px; margin-bottom: 8px; object-fit: contain;" />` : ""}
          ${businessNameEn ? `<div style="font-weight: bold; font-size: 18px; color: #1f2937;">${businessNameEn}</div>` : ""}
          ${businessNameAr && businessNameAr !== businessNameEn ? `<div style="font-weight: bold; font-size: 18px; color: #1f2937;">${businessNameAr}</div>` : ""}
          <div style="font-weight: bold; font-size: 16px; color: #1f2937; margin-top: 8px;">${t("Balance Sheet Report")}</div>
          <div style="font-size: 12px; color: #555; margin-top: 5px;">${t("From")}: ${payload?.start_date} - ${t("To")}: ${payload?.end_date}</div>
        </div>

        <!-- Column 3: Contact Info (Mirrored) -->
        <div style="flex: 1; text-align: end; font-size: 13px; color: #333; line-height: 1.6;">
          ${businessAddress ? `<div><span style="font-weight: bold;">${t("Address")}:</span> ${businessAddress}</div>` : ""}
          ${userMobile ? `<div><span style="font-weight: bold;">${t("Phone")}:</span> ${userMobile}</div>` : ""}
          ${userEmail ? `<div><span style="font-weight: bold;">${t("Email")}:</span> ${userEmail}</div>` : ""}
        </div>
        
      </div>
      `);

      doc.write(assetsHTML + equityHTML + liabilitiesHTML);
      doc.write("</body></html>");
      doc.close();

      iframe.onload = () => {
        iframe.contentWindow.print();
      };
    }, 1000);
  };

  const generateExcelData = () => {
    const data = [];

    const flattenAccounts = (nodes = [], level = 0) => {
      let result = [];
      nodes.forEach((node) => {
        result.push({ ...node, level });
        if (node.children && node.children.length > 0) {
          result = result.concat(flattenAccounts(node.children, level + 1));
        }
      });
      return result;
    };

    const addSection = (title, accounts = [], total = 0) => {
      data.push([title]);
      data.push([
        t("name") || "Name",
        t("credit") || "Credit",
        t("debit") || "Debit",
        t("balance") || "Balance",
      ]);

      const flatAccounts = flattenAccounts(accounts);
      flatAccounts.forEach((row) => {
        const indent = "    ".repeat(row.level || 0); // using 4 spaces per level for visual hierarchy
        const nameCode = `${indent}${row.account_name || ""}-${row.account_code || ""}`;
        const credit = `${row.total_credit ?? "0"} ${t("SAR")}`;
        const debit = `${row.total_debit ?? "0"} ${t("SAR")}`;
        const balance = `${row.balance ? Number(row.balance).toFixed(2) : "0"} ${t("SAR")}`;
        data.push([nameCode, credit, debit, balance]);
      });

      data.push([
        `${title} ${t("Total") || "Total"}`,
        "",
        "",
        `${total ? Number(total).toFixed(2) : "0"} ${t("SAR")}`,
      ]);
      data.push([]);
    };

    addSection(
      t("Assets"),
      balanceSheet?.data?.Assets?.Accounts || [],
      balanceSheet?.data?.Assets?.Total,
    );
    addSection(
      t("Equity"),
      balanceSheet?.data?.Equity?.Accounts || [],
      balanceSheet?.data?.Equity?.Total,
    );
    addSection(
      t("Liabilities"),
      balanceSheet?.data?.Liabilities?.Accounts || [],
      balanceSheet?.data?.Liabilities?.Total,
    );

    return data;
  };

  const handleExcelExport = () => {
    setTimeout(() => {
      const reportTitle = t("Balance Sheet Report") || "Balance Sheet Report";
      const dateRange =
        payload?.start_date || payload?.end_date
          ? `${t("From")}: ${payload?.start_date || ""}  -  ${t("To")}: ${payload?.end_date || ""}`
          : "";
      const businessNameEn = user?.Business?.name_en || "";
      const businessNameAr = user?.Business?.name_ar || "";
      const displayBusinessName =
        lang === "ar"
          ? businessNameAr || businessNameEn
          : businessNameEn || businessNameAr;

      const wb = XLSX.utils.book_new();

      const headerAoa = [
        [`${displayBusinessName}`],
        [`${reportTitle}`],
        [`${dateRange}`],
        [],
      ];

      const sectionsData = generateExcelData();
      const aoa = [...headerAoa, ...sectionsData];

      const ws = XLSX.utils.aoa_to_sheet(aoa);

      let maxCols = 4;
      ws["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: maxCols - 1 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: maxCols - 1 } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: maxCols - 1 } },
      ];

      for (let r = 0; r < 3; r++) {
        const cellRef = XLSX.utils.encode_cell({ c: 0, r });
        if (ws[cellRef]) {
          ws[cellRef].s = {
            font: { bold: true, sz: r === 0 ? 16 : 14 },
            alignment: { horizontal: "center", vertical: "center" },
          };
        }
      }

      const nameHeader = t("name") || "Name";
      const totalText = t("Total") || "Total";

      aoa.forEach((row, rowIndex) => {
        if (row.length === 1 && row[0] && rowIndex >= 4) {
          const cellRef = XLSX.utils.encode_cell({ c: 0, r: rowIndex });
          if (ws[cellRef]) {
            ws[cellRef].s = {
              font: { bold: true, sz: 14 },
              alignment: { horizontal: "left", vertical: "center" },
            };
          }
          ws["!merges"].push({
            s: { r: rowIndex, c: 0 },
            e: { r: rowIndex, c: maxCols - 1 },
          });
        } else if (row.length === 4 && row[0] === nameHeader) {
          for (let c = 0; c < 4; c++) {
            const cellRef = XLSX.utils.encode_cell({ c, r: rowIndex });
            if (ws[cellRef]) {
              ws[cellRef].s = {
                fill: { patternType: "solid", fgColor: { rgb: "D1D5DB" } },
                font: { bold: true },
                alignment: { horizontal: "center", vertical: "center" },
              };
            }
          }
        } else if (row.length === 4 && row[0] && row[0].includes(totalText)) {
          const cellRef1 = XLSX.utils.encode_cell({ c: 0, r: rowIndex });
          const cellRef2 = XLSX.utils.encode_cell({ c: 3, r: rowIndex });
          if (ws[cellRef1]) {
            ws[cellRef1].s = {
              font: { bold: true },
              alignment: { horizontal: "right", vertical: "center" },
            };
          }
          if (ws[cellRef2]) {
            ws[cellRef2].s = {
              font: { bold: true },
              alignment: { horizontal: "center", vertical: "center" },
            };
          }
          ws["!merges"].push({
            s: { r: rowIndex, c: 0 },
            e: { r: rowIndex, c: 2 },
          });
        }
      });

      const colWidths = [{ wch: 40 }, { wch: 20 }, { wch: 20 }, { wch: 20 }];
      ws["!cols"] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, reportTitle.substring(0, 31));
      XLSX.writeFile(wb, `${reportTitle}.xlsx`);
    }, 1000);
  };

  const handleSubmit = () => {
    executeFetch(
      "POST",
      `reports/balance_sheet?start_date=${payload?.start_date}&end_date=${payload?.end_date}`,
    );
  };
  const handleReset = () => {
    const today = new Date(
      new Date().getTime() - new Date().getTimezoneOffset() * 60000,
    )
      .toISOString()
      .split("T")[0];
    const updatedPayload = {
      ...payload,
      start_date: getOneMonthAgo(today),
      end_date: today,
    };
    executeFetch(
      "POST",
      `reports/balance_sheet?start_date=${updatedPayload?.start_date}&end_date=${updatedPayload?.end_date}`,
    );
    setPayload(updatedPayload);
  };
  // useEffect(() => {
  //   executeFetch("POST", `reports/balance_sheet?start_date=${payload?.start_date}&end_date=${payload?.end_date}`);
  // }, [executeFetch]);

  return (
    <>
      <div className="flex justify-end">
        <div className="flex gap-2 flex-col sm:flex-row">
          <DatePicker
            useFormik={false}
            value={payload?.start_date ? new Date(payload.start_date) : null}
            onChange={(e) => {
              setPayload({
                ...payload,
                start_date: e.value
                  ? new Date(
                      e.value.getTime() - e.value.getTimezoneOffset() * 60000,
                    )
                      .toISOString()
                      .split("T")[0]
                  : "",
              });
            }}
          />
          <DatePicker
            useFormik={false}
            value={payload?.end_date ? new Date(payload.end_date) : null}
            onChange={(e) => {
              setPayload({
                ...payload,
                end_date: e.value
                  ? new Date(
                      e.value.getTime() - e.value.getTimezoneOffset() * 60000,
                    )
                      .toISOString()
                      .split("T")[0]
                  : "",
              });
            }}
          />
          <div className=" flex justify-center gap-2 items-center">
            <SharedButton
              title={t("apply_filter")}
              icon="pi pi-filter"
              onClick={handleSubmit}
              loading={isLoading}
              disabled={!payload?.end_date || !payload?.start_date}
            />
            <SharedButton
              title={t("Reset")}
              icon="pi pi-sync"
              buttonType="reset"
              onClick={handleReset}
              loading={isLoading}
            />
            <SharedButton
              title={t("Export Excel")}
              icon={"pi pi-file-excel"}
              iconSize={20}
              onClick={() => handleExcelExport()}
            />
            <SharedButton
              title={t("Export PDF")}
              icon={"pi pi-print"}
              iconSize={20}
              onClick={() => handlePrint()}
            />
          </div>
        </div>
      </div>

      <div className="this_div" ref={balanceSheetRef}>
        <div className="!pb-8 mt-4">
          <CustomTabs
            tabs={[
              {
                name: `${t("Assets")} (${balanceSheet?.data?.Assets?.Total ? balanceSheet?.data?.Assets?.Total?.toFixed(2) : "0"} ${t("SAR")})`,
                content: (
                  <div className="one">
                    <CustomBalanceSheetTreeTable data={balanceSheet} />
                    <div className="flex justify-end mt-2">
                      <div className="mx-16 flex gap-2">
                        <span className="font-semibold text-black/70 text-lg">{`${t("assets")} ${t("Total")}`}</span>
                        <span className="font-semibold text-black/60 text-lg">
                          {balanceSheet?.data?.Assets?.Total
                            ? balanceSheet?.data?.Assets?.Total?.toFixed(2)
                            : "0"}{" "}
                          <span>{t("SAR")}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                name: `${t("Equity")} (${balanceSheet?.data?.Equity?.Total ? balanceSheet?.data?.Equity?.Total?.toFixed(2) : "0"} ${t("SAR")})`,
                content: (
                  <div className="two">
                    <Equity data={balanceSheet} />
                    <div className="flex justify-end mt-2">
                      <div className="mx-16 flex gap-2">
                        <span className="font-semibold text-black/70 text-lg">{`${t("Equity")} ${t("Total")}`}</span>
                        <span className="font-semibold text-black/60 text-lg">
                          {balanceSheet?.data?.Equity?.Total
                            ? balanceSheet?.data?.Equity?.Total?.toFixed(2)
                            : "0"}{" "}
                          <span>{t("SAR")}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                name: `${t("Liabilities")} (${balanceSheet?.data?.Liabilities?.Total ? balanceSheet?.data?.Liabilities?.Total?.toFixed(2) : "0"} ${t("SAR")})`,
                content: (
                  <div className="three">
                    <Liabilities data={balanceSheet} />
                    <div className="flex justify-end mt-2">
                      <div className="mx-16 flex gap-2">
                        <span className="font-semibold text-black/70 text-lg">{`${t("Liabilities")} ${t("Total")}`}</span>
                        <span className="font-semibold text-black/60 text-lg">
                          {balanceSheet?.data?.Liabilities?.Total
                            ? balanceSheet?.data?.Liabilities?.Total?.toFixed(2)
                            : "0"}{" "}
                          <span>{t("SAR")}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                ),
              },
            ]}
            activeTabIndex={activeTab}
            setActiveTabIndex={setActiveTab}
          />
        </div>
      </div>

      <iframe ref={iframeRef} style={{ display: "none" }}></iframe>
    </>
  );
}
